import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * DevDebugPanel — visible only in development (import.meta.env.DEV).
 * Fixed bottom-right panel to test PLG gating without DB manipulation.
 */

// Global override store — read by useSubscription via window
declare global {
  interface Window {
    __DEV_PLAN_OVERRIDE__?: "essentiel" | "pro" | null;
    __DEV_RECETTES_OVERRIDE__?: number | null;
  }
}

const C = {
  navy: "#1B3A5C",
  teal: "#2A9D8F",
  bg: "#0F172A",
  border: "#334155",
  muted: "#94A3B8",
  white: "#F8FAFC",
  danger: "#EF4444",
  success: "#22C55E",
};

export default function DevDebugPanel() {
  const [open, setOpen] = useState(false);
  const [planOverride, setPlanOverride] = useState<"essentiel" | "pro" | null>(
    window.__DEV_PLAN_OVERRIDE__ ?? null
  );
  const [recettesOverride, setRecettesOverride] = useState("");
  const [iaResetLoading, setIaResetLoading] = useState(false);
  const [simCount, setSimCount] = useState({ simulateur: 0, projection: 0 });
  const { user } = useAuth();

  const refreshSimCounts = () => {
    const now = new Date();
    const prefix = `psygestion_sim_uses_${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, "0")}`;
    setSimCount({
      simulateur: Number(localStorage.getItem(`${prefix}_simulateur`) || "0"),
      projection: Number(localStorage.getItem(`${prefix}_projection`) || "0"),
    });
  };

  useEffect(() => {
    refreshSimCounts();
  }, [open]);

  // Don't render in production
  if (!import.meta.env.DEV) return null;

  const handlePlanToggle = (plan: "essentiel" | "pro") => {
    const newVal = planOverride === plan ? null : plan;
    setPlanOverride(newVal);
    window.__DEV_PLAN_OVERRIDE__ = newVal;
    // Force re-render across components
    window.dispatchEvent(new CustomEvent("dev-plan-change"));
  };

  const handleResetIaTrial = async () => {
    if (!user) return;
    setIaResetLoading(true);
    await supabase
      .from("profils")
      .update({ ia_trial_used: 0 } as any)
      .eq("user_id", user.id);
    // Also clear localStorage fallback
    localStorage.removeItem("assistant_first_message_used");
    setIaResetLoading(false);
    window.dispatchEvent(new CustomEvent("dev-plan-change"));
    alert("✅ ia_trial_used reset to 0. Refresh the assistant tab.");
  };

  const handleRecettesOverride = () => {
    const val = Number(recettesOverride);
    if (isNaN(val) || val < 0) return;
    window.__DEV_RECETTES_OVERRIDE__ = val;
    window.dispatchEvent(new CustomEvent("dev-plan-change"));
  };

  const handleClearRecettesOverride = () => {
    window.__DEV_RECETTES_OVERRIDE__ = null;
    setRecettesOverride("");
    window.dispatchEvent(new CustomEvent("dev-plan-change"));
  };

  const handleResetSimulations = (key: string) => {
    const now = new Date();
    const prefix = `psygestion_sim_uses_${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, "0")}`;
    localStorage.removeItem(`${prefix}_${key}`);
    refreshSimCounts();
    alert(`✅ Compteur ${key} remis à 0. Rouvrez l'onglet.`);
  };

  return (
    <>
      {/* Toggle button */}
      <button
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-4 right-4 z-[9999] w-10 h-10 rounded-full flex items-center justify-center text-sm shadow-lg transition-transform hover:scale-110"
        style={{ backgroundColor: C.teal, color: C.white }}
        title="Dev Debug Panel"
      >
        🛠
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-16 right-4 z-[9999] w-80 rounded-xl shadow-2xl overflow-hidden text-sm"
          style={{ backgroundColor: C.bg, border: `1px solid ${C.border}`, color: C.white }}
        >
          {/* Header */}
          <div className="px-4 py-2.5 flex items-center justify-between" style={{ borderBottom: `1px solid ${C.border}` }}>
            <span className="font-bold text-xs tracking-wider" style={{ color: C.teal }}>🛠 DEV DEBUG PANEL</span>
            <button onClick={() => setOpen(false)} className="text-xs" style={{ color: C.muted }}>✕</button>
          </div>

          <div className="p-4 space-y-4">
            {/* Plan Override */}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: C.muted }}>
                Plan Override {planOverride && <span style={{ color: C.danger }}>(actif)</span>}
              </label>
              <div className="flex gap-2">
                {(["essentiel", "pro"] as const).map(p => (
                  <button
                    key={p}
                    onClick={() => handlePlanToggle(p)}
                    className="flex-1 py-1.5 rounded-lg text-xs font-bold transition-colors"
                    style={{
                      backgroundColor: planOverride === p ? (p === "pro" ? C.teal : C.danger) : "transparent",
                      color: planOverride === p ? C.white : C.muted,
                      border: `1px solid ${planOverride === p ? "transparent" : C.border}`,
                    }}
                  >
                    {p === "essentiel" ? "Essentiel" : "Pro"}
                  </button>
                ))}
              </div>
              {planOverride && (
                <button
                  onClick={() => { setPlanOverride(null); window.__DEV_PLAN_OVERRIDE__ = null; window.dispatchEvent(new CustomEvent("dev-plan-change")); }}
                  className="mt-1 text-[10px] underline"
                  style={{ color: C.muted }}
                >
                  Réinitialiser (utiliser la vraie valeur DB)
                </button>
              )}
            </div>

            {/* IA Trial Reset */}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: C.muted }}>
                Assistant IA Trial
              </label>
              <button
                onClick={handleResetIaTrial}
                disabled={iaResetLoading || !user}
                className="w-full py-1.5 rounded-lg text-xs font-bold transition-colors disabled:opacity-40"
                style={{ backgroundColor: "#7C3AED", color: C.white }}
              >
                {iaResetLoading ? "⏳ Reset..." : "🔄 Reset ia_trial_used → 0"}
              </button>
            </div>

            {/* Recettes Override */}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: C.muted }}>
                Recettes ce mois (override)
                {window.__DEV_RECETTES_OVERRIDE__ != null && <span style={{ color: C.danger }}> = {window.__DEV_RECETTES_OVERRIDE__}</span>}
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  placeholder="ex: 10"
                  value={recettesOverride}
                  onChange={e => setRecettesOverride(e.target.value)}
                  className="flex-1 rounded-lg px-3 py-1.5 text-xs outline-none"
                  style={{ backgroundColor: "#1E293B", color: C.white, border: `1px solid ${C.border}` }}
                />
                <button
                  onClick={handleRecettesOverride}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold"
                  style={{ backgroundColor: C.teal, color: C.white }}
                >
                  Set
                </button>
                {window.__DEV_RECETTES_OVERRIDE__ != null && (
                  <button
                    onClick={handleClearRecettesOverride}
                    className="px-2 py-1.5 rounded-lg text-xs"
                    style={{ backgroundColor: C.danger, color: C.white }}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            {/* Simulation counters */}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: C.muted }}>
                Simulations ce mois
              </label>
              <div className="space-y-1.5">
                {(["simulateur", "projection"] as const).map(key => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: C.muted }}>
                      {key}: <strong style={{ color: simCount[key] >= 3 ? C.danger : C.success }}>{simCount[key]}/3</strong>
                    </span>
                    <button
                      onClick={() => handleResetSimulations(key)}
                      className="text-[10px] px-2 py-0.5 rounded"
                      style={{ backgroundColor: "#1E293B", color: C.muted, border: `1px solid ${C.border}` }}
                    >
                      Reset
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* User info */}
            <div className="pt-2" style={{ borderTop: `1px solid ${C.border}` }}>
              <p className="text-[10px]" style={{ color: C.muted }}>
                User: {user?.email ?? "non connecté"}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
